import React, { createContext, useContext } from "react";
import { ensure, getById } from "sprint-common-web";

import { TvSeriesDashboardContext } from "../../../TvSeriesDashboardProvider";
import { EpisodeDetails, EpisodeVersionDetails, TvSeasonDetails, TvSeriesDetails } from "../../../TvSeriesDetails";
import { TvSeriesContext } from "../../context/TvSeriesContext";
import { UpdateEpisodeVersionParams } from "../version-listing/edit-version-form/EditEpisodeVersionForm";

interface IEpisodePageContext {
    selectedTvSeries: TvSeriesDetails;
    selectedEpisode: EpisodeDetails;
    selectedSeason: TvSeasonDetails;
    updateEpisode(title: string, number: number): void;
    addVersion(versionDetails: EpisodeVersionDetails): void;
    updateVersion(updatedVersionData: UpdateEpisodeVersionParams): void;
    setVersionAsMerged(versionId: string, mergedTargetId: string): void;
}

const defaultEpisodePageContext: IEpisodePageContext = {} as IEpisodePageContext;

export const EpisodePageContext = createContext<IEpisodePageContext>(defaultEpisodePageContext);

type EpisodePageDataProviderProps = React.PropsWithChildren<unknown>;

export const EpisodePageProvider: React.FunctionComponent<EpisodePageDataProviderProps> = ({
    children
}: EpisodePageDataProviderProps) => {
    const tvSeriesContext = useContext(TvSeriesContext);
    const tvSeries = tvSeriesContext.tvSeries;
    const { selectedSeason, selectedEpisode, selectCurrentEpisode } = useContext(TvSeriesDashboardContext);

    ensure(tvSeries);
    ensure(selectedSeason);
    ensure(selectedEpisode);

    const season = getById(tvSeries.seasons, selectedSeason.id);
    const episode = getById(season.episodes, selectedEpisode.id);

    const updateEpisode = (title: string, number: number) => {
        tvSeriesContext.updateEpisode(selectedSeason.id, selectedEpisode.id, title, number);
        selectCurrentEpisode(selectedEpisode.id, title);
    };

    const addVersion = (newVersion: EpisodeVersionDetails) => {
        tvSeriesContext.addEpisodeVersion(selectedSeason.id, selectedEpisode.id, newVersion);
    };
    const updateVersion = (updatedVersion: UpdateEpisodeVersionParams) => {
        tvSeriesContext.updateEpisodeVersion(
            selectedSeason.id,
            selectedEpisode.id,
            updatedVersion.versionId,
            updatedVersion.distributor,
            updatedVersion.code,
            updatedVersion.type
        );
    };

    const setVersionAsMerged = (versionId: string, mergedTargetId: string) => {
        tvSeriesContext.setVersionAsMerged(selectedSeason.id, selectedEpisode.id, versionId, mergedTargetId);
    };

    return (
        <EpisodePageContext.Provider
            value={{
                updateEpisode,
                addVersion,
                updateVersion,
                setVersionAsMerged,
                selectedTvSeries: tvSeries,
                selectedEpisode: episode,
                selectedSeason: season
            }}
        >
            {children}
        </EpisodePageContext.Provider>
    );
};
